import { Alert, Button, Card, DatePicker, Form, Input, Select, message } from 'antd';
import dayjs from 'dayjs';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationSectionSelect,
  EducationStudentSelect,
} from '@/pages/education/components/EducationLookupSelects';

type FormValues = {
  student_id: string;
  section_id?: string;
  screened_on: dayjs.Dayjs;
  domain: string;
  outcome: string;
  summary: string;
  detail_text?: string;
};

export default function InclusionScreeningFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.inclusion.manage') || user?.permissions?.includes('education.admin')
  );

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      api.post<{ id: string }>('/education/inclusion/screenings', {
        student_id: values.student_id,
        section_id: values.section_id || undefined,
        screened_on: values.screened_on.format('YYYY-MM-DD'),
        domain: values.domain,
        outcome: values.outcome,
        summary: values.summary,
        detail_text: values.detail_text,
      }),
    onSuccess: (res) => {
      message.success(t('screeningCreated'));
      const id = res.data?.id;
      navigate(id ? `/education/inclusion/screenings/${id}` : '/education/inclusion/screenings');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={t('newScreening')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('newScreening')}
      description={t('inclusionScreeningsIntro')}
      extra={<Button onClick={() => navigate('/education/inclusion/screenings')}>{t('inclusionScreenings')}</Button>}
    >
      <Card size="small" style={{ maxWidth: 640 }}>
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={t('inclusionNonDiagnosticHint')} />
        <Form
          layout="vertical"
          initialValues={{ screened_on: dayjs(), domain: 'learning', outcome: 'monitor' }}
          onFinish={(values) => create.mutate(values)}
        >
          <Form.Item name="student_id" label={t('student')} rules={[{ required: true }]}>
            <EducationStudentSelect />
          </Form.Item>
          <Form.Item name="section_id" label={t('section')}>
            <EducationSectionSelect allowClear />
          </Form.Item>
          <Form.Item name="screened_on" label={t('screenedOn')} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
          </Form.Item>
          <Form.Item name="domain" label={t('screeningDomain')} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'attention', label: t('screeningDomainAttention') },
                { value: 'learning', label: t('screeningDomainLearning') },
                { value: 'social_emotional', label: t('screeningDomainSocial') },
                { value: 'behaviour_observation', label: t('screeningDomainBehaviour') },
                { value: 'speech_language', label: t('screeningDomainSpeech') },
                { value: 'motor', label: t('screeningDomainMotor') },
                { value: 'other', label: t('screeningDomainOther') },
              ]}
            />
          </Form.Item>
          <Form.Item name="outcome" label={t('screeningOutcome')} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'no_concern', label: t('screeningOutcomeNone') },
                { value: 'monitor', label: t('screeningOutcomeMonitor') },
                { value: 'refer_for_support', label: t('screeningOutcomeRefer') },
                { value: 'consent_pending', label: t('screeningOutcomeConsent') },
              ]}
            />
          </Form.Item>
          <Form.Item name="summary" label={t('summary')} rules={[{ required: true }]}>
            <Input maxLength={500} />
          </Form.Item>
          <Form.Item name="detail_text" label={t('detail')}>
            <Input.TextArea rows={3} maxLength={2000} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={create.isPending}>
            {t('save')}
          </Button>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
